import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { salesHubApi } from '@/services/api';
import { X, AlertTriangle, Loader2, Trash2, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { formatMoney } from '@/utils/currency';
import toast from 'react-hot-toast';

interface Props {
  invoiceId: string;
  invoiceName?: string;
  customerName?: string;
  totalAmount?: number;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function DeleteSaleModal({
  invoiceId,
  invoiceName,
  customerName,
  totalAmount,
  onClose,
  onSuccess,
}: Props) {
  useEscapeToClose(onClose);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const deleteSaleMutation = useMutation({
    mutationFn: (pwd: string) => salesHubApi.deleteSaleInvoice(invoiceId, pwd),
    onSuccess: (res: any) => {
      const msg = res?.data?.message?.message || 'Sale deleted successfully and stock reverted.';
      toast.success(msg);
      queryClient.invalidateQueries({ queryKey: ['salesHubList'] });
      queryClient.invalidateQueries({ queryKey: ['salesHubCounts'] });
      queryClient.invalidateQueries({ queryKey: ['salesHubDetail'] });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['stock'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      if (onSuccess) onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(
        err?.response?.data?.message ||
        err?.response?.data?._server_messages ||
        'Failed to delete sale. Please check your password.'
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError('Admin password is required to delete this sale.');
      return;
    }
    setError('');
    deleteSaleMutation.mutate(password);
  };

  const displayName = invoiceName || invoiceId;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-rose-100 bg-rose-50/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center text-rose-600 shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-base">Delete Sale & Revert Stock</h3>
              <p className="text-xs text-rose-700 font-mono mt-0.5">{displayName}</p>
              <p className="text-[11px] text-slate-500 mt-1">Logged for audit (who / when / amount)</p>
            </div>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="p-1.5 hover:bg-rose-100 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Sale summary badge */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 flex items-center justify-between text-xs">
            <div>
              <span className="text-slate-500 block">Customer</span>
              <span className="font-semibold text-slate-800">{customerName || 'Walk-in'}</span>
            </div>
            {totalAmount !== undefined && (
              <div className="text-right">
                <span className="text-slate-500 block">Total</span>
                <span className="font-bold text-slate-900 text-sm">{formatMoney(totalAmount)}</span>
              </div>
            )}
          </div>

          <div className="bg-amber-50/80 border border-amber-200/80 rounded-xl p-3.5 space-y-2 text-xs text-amber-900">
            <div className="font-semibold flex items-center gap-1.5 text-amber-800">
              <RotateCcw className="w-4 h-4 text-amber-600 shrink-0" />
              <span>Actions performed upon deletion:</span>
            </div>
            <ul className="list-disc list-inside space-y-1 text-amber-800/90 pl-1">
              <li>Item stock quantities are restored to inventory.</li>
              <li>Payment entries and cash ledgers are cancelled/deleted.</li>
              <li>Shift sales totals and drawer counts are adjusted.</li>
              <li>Customer receivables balance is updated to zero.</li>
            </ul>
          </div>

          {/* Password confirmation */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Enter Admin Password to confirm:
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                autoFocus
                placeholder="Admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-xl px-3.5 py-2.5 pr-10 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none transition-all placeholder:text-slate-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 focus:outline-none"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={deleteSaleMutation.isPending}
              className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={deleteSaleMutation.isPending || !password}
              className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors shadow-sm flex items-center gap-2"
            >
              {deleteSaleMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              <span>{deleteSaleMutation.isPending ? 'Deleting & Reverting...' : 'Delete Sale & Revert Stock'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
